import {useLazyQuery, useMutation} from '@apollo/client'
import {useEffect, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {GET_ONE_VALIDATION} from '../../graphql/queries/validations'
import {IResAllValidations, IResSolicitarURL} from '../../graphql/types/validationsTypes'
import {Accordion, Button, Card, OverlayTrigger, Popover, Spinner} from 'react-bootstrap'
import moment from 'moment'
import {BadgeStatus} from '../oneUser/components/activityTab/BadgeStatus'
import {parseId} from '../../helpers'
import {useDisclosure} from '../../hooks/useDisclosure'
import {ModalAddValidation} from './components/ModalAddValidation'
import {ModalAnularValidation} from './components/ModalAnularValidation'
import {STATUS_VALIDATIONS} from '../../helpers/const'
import {SOLICITAR_URL} from '../../graphql/mutations/validationMutations'
import {ModalViewUrl} from './components/ModalViewUrl'
import {LoadingOneValidation} from './components/LoadingOneValidation'

const StepTypes = {
  SIN_PASO: 'SIN PASO',
  CREAR_VALIDACION: 'CREAR VALIDACION',
  FINALIZADO: 'FINALIZADO',
}

export const OneValidationPage = () => {
  const [gqlGetOne, {data, loading, error, refetch}] = useLazyQuery<IResAllValidations>(
    GET_ONE_VALIDATION,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const [gqlSolicitar, {data: dataSolici, loading: loadingSolici, error: errorSolici}] =
    useMutation<IResSolicitarURL>(SOLICITAR_URL)

  const btnRef = useRef<HTMLButtonElement>(null)

  const [url, setUrl] = useState('')

  const {id} = useParams()

  const {isOpen, onClose, onOpen} = useDisclosure()
  const {isOpen: isOpenAnular, onClose: onCloseAnular, onOpen: onOpenAnular} = useDisclosure()
  const {isOpen: isOpenSoli, onClose: onCloseSoli, onOpen: onOpenSoli} = useDisclosure()

  useEffect(() => {
    const num = Number(id)
    if (isNaN(num)) {
      toast.error('Error al cargar información')
      return
    }
    gqlGetOne({
      variables: {
        id,
      },
    })
  }, [gqlGetOne, id])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])
  useEffect(() => {
    if (errorSolici) toast.error(`Error: ${errorSolici.message}`)
  }, [errorSolici])

  useEffect(() => {
    if (!dataSolici) return

    if (dataSolici.identityValidationUrlBuilder.success) {
      setUrl(dataSolici.identityValidationUrlBuilder.url)
      if (btnRef.current) {
        btnRef.current.click()
      }
      onOpenSoli()
    } else if (dataSolici.identityValidationUrlBuilder.error) {
      toast.error(`Error: ${dataSolici.identityValidationUrlBuilder.error.message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSolici])

  const handleSoli = (idValidation: string) => {
    gqlSolicitar({
      variables: {
        input: {
          identityValidationId: parseId(idValidation),
        },
      },
    })
  }

  if (loading || !data) {
    return <LoadingOneValidation />
  }

  const {node} = data.identityValidationList?.edges?.[0]

  if (!node) {
    return <div>NOT FOUND</div>
  }

  const popover = (
    <Popover id='popover-basic'>
      <Popover.Header as='h3'>Solicitar URL</Popover.Header>
      {node.status === STATUS_VALIDATIONS.CREADA ? (
        <Popover.Body>
          <h6>¿Seguro que deseas solicitar URL?</h6>
          <div className='d-flex algin-items-center justify-content-center'>
            <Button
              variant='primary'
              size='sm'
              disabled={loadingSolici}
              onClick={() => handleSoli(node.id)}
            >
              {loadingSolici && <Spinner animation='border' size='sm' className='me-2' />}
              Solicitar
            </Button>
          </div>
        </Popover.Body>
      ) : (
        <Popover.Body>No se puede solicitar URL a esta validación</Popover.Body>
      )}
    </Popover>
  )

  return (
    <div style={{marginTop: '-20px'}}>
      <Card className='mb-4 shadow-sm'>
        <Card.Header className=''>
          <Card.Title className=''>Información del Usuario</Card.Title>
          <div className='card-title d-flex align-items-center gap-2'>
            <Button
              size='sm'
              variant='outline-primary'
              disabled={node.status !== STATUS_VALIDATIONS.CREADA}
              onClick={() => {
                if (node.status === STATUS_VALIDATIONS.CREADA) {
                  onOpen()
                }
              }}
            >
              <i className='bi bi-pencil' style={{color: 'inherit'}} />
            </Button>
            <Button
              variant='danger'
              size='sm'
              disabled={
                node.status === 'ANULADA' ||
                node.status === 'EXITOSA' ||
                node.status === 'RECHAZADA'
              }
              onClick={() => {
                if (
                  node.status !== 'ANULADA' &&
                  node.status !== 'EXITOSA' &&
                  node.status !== 'RECHAZADA'
                ) {
                  onOpenAnular()
                }
              }}
            >
              Anular
            </Button>
            <OverlayTrigger trigger='click' placement='bottom' overlay={popover}>
              <Button variant='info' size='sm' ref={btnRef}>
                Solicitar url
              </Button>
            </OverlayTrigger>
          </div>
        </Card.Header>
        <Card.Body>
          <div className='d-flex align-items-center gap-2 my-3'>
            <i style={{fontSize: '40px'}} className='bi bi-person text-primary' />
            <div>
              <h5>
                {node.firstName} {node.secondName || ''} {node.firstSurname}{' '}
                {node.secondSurname || ''}
              </h5>
              <p className='mb-1 text-muted'>ID: {parseId(node.id)}</p>
            </div>
          </div>

          <Accordion defaultActiveKey={['0', '1', '2']}>
            <Accordion.Item eventKey='0'>
              <Accordion.Header>
                <i className='bi bi-card me-2' /> Detalles del Documento
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  <strong>Tipo de Documento:</strong> {node.documentType}
                </p>
                <p>
                  <strong>Número:</strong> {node.documentNumber}
                </p>
                <p>
                  <strong>Fecha de Expedición:</strong>{' '}
                  {moment(node.expeditionDate).format('DD MMM YYYY')}
                </p>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey='1'>
              <Accordion.Header>
                <i className='bi bi-phone me-2' /> Información de Contacto
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  <strong>Teléfono:</strong> {node.phoneNumber}
                </p>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey='2'>
              <Accordion.Header>
                <i className='bi bi-flag me-2' /> Estado y Proceso
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  <strong className='me-4'>Estado:</strong>
                  <BadgeStatus status={node.status} />
                </p>
                <p>
                  <strong>Paso:</strong> {StepTypes[node.step]}
                </p>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Card.Body>
        <Card.Footer className='text-muted'>
          <i className='bi bi-calendar me-2' /> Creado el{' '}
          {moment(node.created).format('DD MMM YYYY [a las] hh:mm A')}
        </Card.Footer>
      </Card>

      {isOpen && (
        <ModalAddValidation isOpen={isOpen} onClose={onClose} refetch={refetch} info={node} />
      )}
      {isOpenAnular && (
        <ModalAnularValidation
          isOpen={isOpenAnular}
          onClose={onCloseAnular}
          refetch={refetch}
          id={node.id}
        />
      )}

      {isOpenSoli && <ModalViewUrl url={url} isOpen={isOpenSoli} onClose={onCloseSoli} />}
    </div>
  )
}
